import { useState, useRef } from "react";

const SERVICE_OPTIONS = [
  "Residential Layout Development",
  "Villa Plot Development",
  "Property Consultation",
  "Site Visit Assistance",
];

const BUDGET_OPTIONS = [
  "Below 25 Lakhs",
  "25 Lakhs to 50 Lakhs",
  "50 Lakhs to 1 Crore",
  "1 Crore to 3 Crores",
  "3 Crores to 5 Crores",
  "Above 5 Crores",
  "Prefer to Discuss",
];

const WA_NUMBER = "919945124133";

export default function WhatsAppEnquiryForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (fields: Record<string, string>) => {
    const errs: Record<string, string> = {};
    if (!fields.name.trim()) errs.name = "Full name is required.";
    if (!fields.phone.trim()) errs.phone = "Phone number is required.";
    if (fields.brief.length > 500) errs.brief = "Project brief cannot exceed 500 characters.";
    return errs;
  };

  const handleSend = () => {
    if (!formRef.current) return;
    const fd = new FormData(formRef.current);
    const fields = {
      name: (fd.get("name") as string) || "",
      phone: (fd.get("phone") as string) || "",
      email: (fd.get("email") as string) || "",
      service: (fd.get("service") as string) || "",
      location: (fd.get("location") as string) || "",
      budget: (fd.get("budget") as string) || "",
      brief: (fd.get("brief") as string) || "",
    };

    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const message =
      `Hello URBAN INFRA!\n\n` +
      `I am interested in your projects.\n\n` +
      `Here are my details:\n\n` +
      `Name: ${fields.name}\n` +
      `Phone: ${fields.phone}\n` +
      `Email: ${fields.email || "Not provided"}\n` +
      `Service: ${fields.service || "Not specified"}\n` +
      `Location: ${fields.location || "Not specified"}\n` +
      `Budget: ${fields.budget || "Not specified"}\n\n` +
      `Project Brief:\n${fields.brief || "Not provided"}\n\n` +
      `I found you through your website.\n` +
      `Looking forward to your response.`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${WA_NUMBER}?text=${encoded}`, "_blank");
  };

  return (
    <div className="bg-white rounded-2xl border border-[#7FBF3F]/15 shadow-sm p-7 md:p-9 w-full">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 flex items-center justify-center bg-[#EAF5E1] rounded-xl flex-shrink-0">
          <i className="ri-whatsapp-line text-[#25D366] text-xl"></i>
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#1B2A4A] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            WhatsApp Enquiry
          </h3>
          <p className="text-xs text-gray-400">We respond within 2 hours during working hours</p>
        </div>
      </div>

      <div className="h-px bg-[#EAF5E1] my-5"></div>

      <form ref={formRef} className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
        {/* Row 1: Name + Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="name"
              placeholder="Your full name"
              className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7FBF3F] transition-colors ${errors.name ? "border-red-400 bg-red-50" : "border-gray-200"}`}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Phone Number <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              placeholder="Your phone number"
              className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7FBF3F] transition-colors ${errors.phone ? "border-red-400 bg-red-50" : "border-gray-200"}`}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            Email Address
          </label>
          <input
            type="email"
            name="email"
            placeholder="your@email.com"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7FBF3F] transition-colors"
          />
        </div>

        {/* Row 2: Service + Budget */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Service Required
            </label>
            <select
              name="service"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7FBF3F] transition-colors bg-white cursor-pointer"
            >
              <option value="">Select a service</option>
              {SERVICE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Budget Range
            </label>
            <select
              name="budget"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7FBF3F] transition-colors bg-white cursor-pointer"
            >
              <option value="">Select budget</option>
              {BUDGET_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Project Location */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            Project Location
          </label>
          <input
            type="text"
            name="location"
            placeholder="Area and city"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7FBF3F] transition-colors"
          />
        </div>

        {/* Project Brief */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            Project Brief{" "}
            <span className={`font-normal normal-case ${charCount > 500 ? "text-red-500" : "text-gray-400"}`}>
              ({charCount}/500)
            </span>
          </label>
          <textarea
            name="brief"
            rows={4}
            maxLength={500}
            placeholder="Tell us about your requirement, timeline, and goals"
            onChange={(e) => setCharCount(e.target.value.length)}
            className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#7FBF3F] transition-colors resize-none ${errors.brief ? "border-red-400 bg-red-50" : "border-gray-200"}`}
          ></textarea>
          {errors.brief && <p className="text-red-500 text-xs mt-1">{errors.brief}</p>}
        </div>

        {/* WhatsApp Button */}
        <button
          type="button"
          onClick={handleSend}
          className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap mt-1"
        >
          <i className="ri-whatsapp-line text-lg"></i>
          Send Enquiry on WhatsApp
        </button>

        <p className="text-center text-xs text-gray-400 leading-relaxed">
          Your details will be sent directly to our WhatsApp. We usually respond within 2 hours during working hours.
        </p>
      </form>
    </div>
  );
}
